import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { useState, useEffect, useCallback } from 'react';

import { CartResponseModel } from 'src/api/models/cart';
import { getActivePrice } from 'src/api/models/surplus';
import { DetailsFavouritesItemModel } from 'src/api/models/favourites';
import {
  useAddToCart,
  useRemoveFromCart,
  useDeleteFavouritesItem,
  useUpdateProductQuantity,
} from 'src/api/hooks/api-hooks';

import {
  ActiveOperations,
  ActiveOperationType,
} from 'src/sections/favourites/favourite-products/actions-cell';

export type EnhancedFavouritesItemModel = DetailsFavouritesItemModel & {
  quantity: number;
  itemInCart: boolean;
  cartQuantity: number;
  price: number;
  totalPrice: number;
  cartItemId: string;
  originalPrice: number;
};

interface Props {
  cart: CartResponseModel;
  productItems: DetailsFavouritesItemModel[];
  collectionId: string;
}

/**
 * Custom hook to extract favourites cart logic for clean components
 */
export default function useFavouritesProductList({ cart, productItems, collectionId }: Props) {
  // Per-row in-flight mutation tracker, keyed by favourites item id.
  // Multiple rows may be mutating concurrently; each row is cleared explicitly
  // in its own onSuccess/onError handler (no longer tied to the recalc effect).
  const [activeOperations, setActiveOperations] = useState<ActiveOperations>(
    () => new Map()
  );

  const markOperation = useCallback((rowId: string, type: ActiveOperationType) => {
    setActiveOperations((prev) => {
      const next = new Map(prev);
      next.set(rowId, type);
      return next;
    });
  }, []);

  const clearOperation = useCallback((rowId: string) => {
    setActiveOperations((prev) => {
      if (!prev.has(rowId)) return prev;
      const next = new Map(prev);
      next.delete(rowId);
      return next;
    });
  }, []);

  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState<EnhancedFavouritesItemModel[]>(() => {
    const allProducts = cart.baskets.flatMap((basket) => basket.items);
    return productItems.map((product) => {
      const cartItem = allProducts.find((i) => i.productStockId === product.productStockId);
      const itemInCart = !!cartItem;
      const quantity = itemInCart ? parseFloat(cartItem.quantity) : 1;

      const price = getActivePrice(product.surplusEntry, product.price);

      return {
        ...product,
        quantity,
        itemInCart,
        originalPrice: product.price,
        price,
        // pricePerUnit: Number(p.price),
        totalPrice: price * quantity,
        cartQuantity: quantity,
        cartItemId: cartItem?.id || '',
      };
    });
  });

  // We copy the product response to a local var in order to track quantity and cart status.
  useEffect(() => {
    if (productItems && productItems?.length > 0 && cart) {
      const allProducts = cart.baskets.flatMap((basket) => basket.items);
      const newFavProducts = productItems.map((product) => {
        const cartItem = allProducts.find((i) => i.productStockId === product.productStockId);
        const itemInCart = !!cartItem;
        const quantity = itemInCart ? parseFloat(cartItem.quantity) : 1;

        const price = getActivePrice(product.surplusEntry, product.price);

        const localFavouritesItem = items?.find(
          (lp) => lp.productStockId === product.productStockId
        );
        // if localProduct quantity is different than quantity use local product quantity
        if (localFavouritesItem && localFavouritesItem.quantity !== quantity) {
          return {
            ...product,
            price,
            quantity: localFavouritesItem.quantity,
            originalPrice: product.price,
            totalPrice: localFavouritesItem.price * localFavouritesItem.quantity,
            itemInCart,
            cartQuantity: quantity,
            cartItemId: cartItem?.id || '',
          };
        }
        return {
          ...product,
          quantity,
          itemInCart,
          price,
          originalPrice: product.price,
          totalPrice: price * quantity,
          // pricePerUnit: Number(p.price),
          cartQuantity: quantity,
          cartItemId: cartItem?.id || '',
          orderPrice: price * Math.max(1, quantity!),
        };
      });
      setItems(newFavProducts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, productItems]);

  const handleQuantityChange = (productStockId: string, delta: number) => {
    const allProducts = cart.baskets.flatMap((basket) => basket.items);
    setItems((prevItems) =>
      prevItems.map((product) => {
        const cartItem = allProducts.find((i) => i.productStockId === product.productStockId);
        const itemInCart = !!cartItem;
        const cartQuantity = itemInCart ? parseFloat(cartItem.quantity) : 1;

        const price = getActivePrice(product.surplusEntry, product.price);

        if (product.productStockId === productStockId) {
          const newQuantity = Math.max(1, product.quantity + delta);
          return {
            ...product,
            quantity: newQuantity,
            totalPrice: price * newQuantity,
          };
        }
        return {
          ...product,
          originalPrice: price,
          quantity: product.quantity,
          totalPrice: price * product.quantity,
          itemInCart,
          cartQuantity,
          cartItemId: cartItem?.id || '',
        };
      })
    );
  };

  const { mutate: addToCart, isLoading: addToCartLoading } = useAddToCart();
  const { mutate: removeFromCart, isLoading: removeFromCartLoading } = useRemoveFromCart();
  const { mutate: updateQuantity, isLoading: updateQuantityLoading } = useUpdateProductQuantity();
  const { mutate: deleteFavouritesItem } = useDeleteFavouritesItem();
  const queryClient = useQueryClient();

  const handleAddToCart = useCallback(
    (productStockId: string, quantity: number, productId: string) => {
      markOperation(productId, 'add');
      addToCart(
        {
          body: {
            productStockId,
            quantity,
          },
        },
        {
          onSuccess: (response) => {
            enqueueSnackbar('Successfully added to basket');
            // TODO: undefined here is a hack
            // We should remove the second key entry from the useGetCart api hook
            queryClient.setQueryData(['cart'], response);
            clearOperation(productId);
          },
          onError: () => {
            clearOperation(productId);
          },
        }
      );
    },
    [addToCart, clearOperation, enqueueSnackbar, markOperation, queryClient]
  );

  const handleRemoveFromCart = useCallback(
    (cartItemId: string, productId: string) => {
      markOperation(productId, 'remove');
      removeFromCart(
        {
          pathParams: {
            itemId: cartItemId,
          },
        },
        {
          onSuccess: (response) => {
            enqueueSnackbar('Removed from basket.');
            queryClient.setQueryData(['cart'], response);
            clearOperation(productId);
          },
          onError: () => {
            clearOperation(productId);
          },
        }
      );
    },
    [clearOperation, enqueueSnackbar, markOperation, queryClient, removeFromCart]
  );

  const handleUpdate = useCallback(
    (cartItemId: string, quantity: number, productId: string) => {
      markOperation(productId, 'update');
      updateQuantity(
        {
          body: {
            quantity,
          },
          pathParams: {
            itemId: cartItemId,
          },
        },
        {
          onSuccess: (responseQuantity) => {
            enqueueSnackbar('Successfully updated quantity');
            queryClient.setQueryData(['cart'], (oldData: any) => {
              if (!oldData) return oldData;
              return {
                ...oldData,
                baskets: oldData.baskets.map((basket: any) => ({
                  ...basket,
                  items: basket.items.map((item: any) =>
                    item.id === cartItemId ? { ...item, quantity: responseQuantity } : item
                  ),
                })),
              };
            });
            clearOperation(productId);
          },
          onError: () => {
            clearOperation(productId);
          },
        }
      );
    },
    [clearOperation, enqueueSnackbar, markOperation, queryClient, updateQuantity]
  );

  const deleteItem = async (itemId: string) => {
    deleteFavouritesItem(
      { pathParams: { collectionId, itemId } },
      {
        onSuccess: () => {
          enqueueSnackbar('Product removed from favourites');
          queryClient.invalidateQueries('favourites');
        },
      }
    );
  };

  return {
    handleQuantityChange,
    handleAddToCart,
    handleRemoveFromCart,
    handleUpdate,
    deleteItem,
    activeOperations,
    items,
    addToCartLoading,
    removeFromCartLoading,
    updateQuantityLoading,
  };
}
